import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { RefreshCw, CheckCircle, XCircle, Users, Phone, Mail } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusClass = (status) => {
  if (status === "confirmed") return "bg-emerald-100 text-emerald-800";
  if (status === "cancelled") return "bg-red-100 text-red-800";
  if (status === "completed") return "bg-slate-100 text-slate-700";
  return "bg-amber-100 text-amber-800";
};

export default function BookingDesk() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [updatingId, setUpdatingId] = useState("");

  const fetchDesk = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/public/booking-desk/${token}`);
      setRestaurant(response.data.restaurant);
      setBookings(response.data.bookings || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking desk not found");
      setRestaurant(null);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesk();
  }, [token]);

  const updateStatus = async (bookingId, status) => {
    setUpdatingId(bookingId);
    try {
      await axios.post(
        `${API_URL}/api/public/booking-desk/${token}/bookings/${bookingId}/status`,
        { status }
      );
      toast.success(`Booking ${status}`);
      await fetchDesk();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    } finally {
      setUpdatingId("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-[#EC1A23]" />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full rounded-none">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            This restaurant booking desk link is invalid or has been rotated.
          </CardContent>
        </Card>
      </div>
    );
  }

  const pending = bookings.filter((item) => item.status === "pending");
  const others = bookings.filter((item) => item.status !== "pending");

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-r from-[#EC1A23] to-[#561D1A] text-white px-4 py-6">
        <div className="max-w-3xl mx-auto flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-white/70">Restaurant booking desk</p>
            <h1 className="text-2xl font-bold font-['Manrope']">{restaurant.name}</h1>
            <p className="text-sm text-white/80 mt-1">
              Accept or decline pending table requests. Guests are notified by WhatsApp and email.
            </p>
          </div>
          <Button variant="secondary" className="rounded-none" onClick={fetchDesk}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <Card className="rounded-none">
          <CardContent className="p-4 text-sm text-muted-foreground flex flex-wrap gap-4">
            <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{restaurant.phone || "No phone"}</span>
            <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{restaurant.booking_notify_email || "No notify email"}</span>
            <span>WhatsApp notify: {restaurant.booking_notify_whatsapp || "Not set"}</span>
          </CardContent>
        </Card>

        <section className="space-y-3">
          <h2 className="font-semibold text-lg">Pending ({pending.length})</h2>
          {pending.length === 0 ? (
            <Card className="rounded-none"><CardContent className="p-6 text-sm text-muted-foreground">No pending requests.</CardContent></Card>
          ) : pending.map((booking) => (
            <Card key={booking.id} className="rounded-none border-[#EC1A23]/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base font-['Manrope']">{booking.reference_id}</CardTitle>
                  <Badge className={statusClass(booking.status)}>{booking.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="font-medium">{booking.customer_name}</p>
                <p className="text-muted-foreground">{booking.customer_phone}{booking.customer_email ? ` · ${booking.customer_email}` : ""}</p>
                <p className="flex items-center gap-2"><Users className="h-4 w-4" />{booking.guests} guests · {booking.booking_date} at {booking.booking_time}</p>
                {booking.special_requests ? <p>Requests: {booking.special_requests}</p> : null}
                <div className="flex gap-2 pt-1">
                  <Button
                    className="btn-gradient rounded-none"
                    disabled={updatingId === booking.id}
                    onClick={() => updateStatus(booking.id, "confirmed")}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-none"
                    disabled={updatingId === booking.id}
                    onClick={() => updateStatus(booking.id, "cancelled")}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="space-y-3">
          <h2 className="font-semibold text-lg">Recent ({others.length})</h2>
          {others.map((booking) => (
            <Card key={booking.id} className="rounded-none">
              <CardContent className="p-4 flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="font-medium">{booking.reference_id} · {booking.customer_name}</p>
                  <p className="text-muted-foreground">{booking.booking_date} {booking.booking_time} · {booking.guests} guests</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={statusClass(booking.status)}>{booking.status}</Badge>
                  {booking.status === "confirmed" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      disabled={updatingId === booking.id}
                      onClick={() => updateStatus(booking.id, "completed")}
                    >
                      Complete
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      </div>
    </div>
  );
}
